import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { 
    Box, Typography, Avatar, Paper, Grid, 
    Container, Divider, Button, Fade, IconButton,
    CircularProgress, Chip
} from '@mui/material';
import {
    School as SchoolIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    Class as ClassIcon,
    Subject as SubjectIcon,
    PhotoCamera as PhotoCameraIcon,
    VpnKey as VpnKeyIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authSuccess } from '../../redux/userRelated/userSlice';
import { useToast } from '../../context/ToastContext';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); } 70% { box-shadow: 0 0 0 15px rgba(99, 102, 241, 0); } 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 40px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -120px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
`;

const AvatarWrapper = styled(Box)`
    position: relative;
    width: 180px;
    height: 180px;
    margin: 0 auto;
    
    .upload-btn {
        position: absolute;
        bottom: 5px;
        right: 5px;
        background: #6366f1;
        color: white;
        transition: all 0.2s ease;
        
        &:hover {
            background: #4f46e5;
            transform: scale(1.1);
        }
        
        &.pulsing {
            animation: ${pulse} 2s infinite;
        }
    }
`;

const InfoBox = styled(Box)`
    padding: 24px;
    background: #f8fafc;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 20px;
    transition: all 0.3s ease;
    
    &:hover {
        background: white;
        box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
        transform: translateY(-2px);
    }
`;

const TeacherProfile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();
    
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [assignments, setAssignments] = useState([]);

    const teachSchool = currentUser?.school;

    useEffect(() => {
        const fetchAssignments = async () => {
            if (currentUser?._id) {
                try {
                    const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Assignments/${currentUser._id}`);
                    if (Array.isArray(result.data)) {
                        setAssignments(result.data);
                    }
                } catch (err) {
                    console.error("Error fetching assignments:", err);
                }
            }
        };
        fetchAssignments();
    }, [currentUser?._id]);

    // Get photo URL or use default avatar
    const photoUrl = currentUser?.photo 
        ? `${process.env.REACT_APP_BASE_URL}/${currentUser.photo}`
        : null;

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadPhoto = async () => {
        if (!photo) return;
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('photo', photo);

            const result = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/ProfilePhotoUpload`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            if (result.data.photo) {
                setPhotoPreview(null);
                setPhoto(null);
                dispatch(authSuccess({
                    ...currentUser,
                    photo: result.data.photo
                }));
                showToast('Profile photo updated successfully!', 'success');
            }
        } catch (error) {
            console.error('Error uploading photo:', error);
            showToast('Error uploading photo. Please try again.', 'error');
        }
        setUploading(false);
    };

    const cancelPhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
    };

    return (
        <PageWrapper>
           <PremiumHeader>
                   <Container maxWidth="xl">
                     <Fade in timeout={800}>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                             <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: 2 }}>
                                Teacher Profile
                             </Typography>
                           </Box>
                           <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400 }}>
                             Review and manage your profile details.
                           </Typography>
                         </Box>
                       </Box>
                     </Fade>
                   </Container>
                 </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{mt: 4}}>
                <Grid container spacing={4}>
                    {/* Left Column - Avatar & Core Identity */}
                    <Grid item xs={12} md={4}>
                        <PremiumCard sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <AvatarWrapper>
                                <Avatar 
                                    alt={currentUser?.name}
                                    src={photoPreview || photoUrl}
                                    sx={{ 
                                        width: 180, 
                                        height: 180,
                                        border: '6px solid white',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                        fontSize: '4rem',
                                        bgcolor: '#6366f1'
                                    }}
                                >
                                    {currentUser?.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                
                                <IconButton
                                    component="label"
                                    className={`upload-btn ${!photoPreview ? 'pulsing' : ''}`}
                                >
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        style={{ display: 'none' }}
                                    />
                                    <PhotoCameraIcon />
                                </IconButton>
                            </AvatarWrapper>

                            {/* Photo Upload/Preview Section */}
                            {photoPreview && (
                                <Fade in>
                                    <Box sx={{ mt: 3, p: 2, bgcolor: '#eff6ff', borderRadius: '16px', border: '1px solid #bfdbfe' }}>
                                        <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600, mb: 2 }}>
                                            New photo selected. Save changes?
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Button 
                                                variant="contained" 
                                                onClick={handleUploadPhoto}
                                                disabled={uploading}
                                                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                                sx={{ bgcolor: '#3b82f6', borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                {uploading ? 'Saving...' : 'Update'}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                color="error"
                                                onClick={cancelPhoto}
                                                startIcon={<CancelIcon />}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                </Fade>
                            )}

                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mt: 4, mb: 1 }}>
                                {currentUser?.name}
                            </Typography>
                            
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#f1f5f9', px: 2, py: 1, borderRadius: '10px' }}>
                                <BadgeIcon sx={{ color: '#64748b', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                                    {currentUser?.role || 'Teacher'}
                                </Typography>
                            </Box>
                        </PremiumCard>
                    </Grid>

                    {/* Right Column - Detailed Information */}
                    <Grid item xs={12} md={8}>
                        <PremiumCard sx={{ p: 5, height: '100%' }}>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                                    Professional Information
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    Your registered details and class assignments.
                                </Typography>
                            </Box>
                            
                            <Divider sx={{ mb: 4, borderColor: '#e2e8f0' }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <InfoBox>
                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 48, height: 48 }}>
                                            <SchoolIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Registered School
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 800 }}>
                                                {teachSchool?.schoolName || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </InfoBox>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InfoBox>
                                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#22c55e', width: 48, height: 48 }}>
                                            <ClassIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Assigned Classes
                                            </Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {assignments.length > 0 ? (
                                                    Array.from(new Set(assignments.map(a => a.sclass?.sclassName))).filter(Boolean).map(className => (
                                                        <Chip key={className} label={className} size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                                                    ))
                                                ) : (
                                                    <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700, wordBreak: 'break-all' }}>
                                                        Not Assigned
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </InfoBox>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InfoBox>
                                        <Avatar sx={{ bgcolor: '#fff7ed', color: '#f97316', width: 48, height: 48 }}>
                                            <SubjectIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Subjects
                                            </Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {assignments.length > 0 ? (
                                                    Array.from(new Set(assignments.map(a => a.subject?.subName))).filter(Boolean).map(subName => (
                                                        <Chip key={subName} label={subName} size="small" sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 600 }} />
                                                    ))
                                                ) : (
                                                    <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700 }}>
                                                        Not Assigned
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </InfoBox>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <InfoBox>
                                        <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6', width: 48, height: 48 }}>
                                            <EmailIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Email Address
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700 }}>
                                                {currentUser?.email || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </InfoBox>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InfoBox sx={{ bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                                        <Avatar sx={{ bgcolor: '#f1f5f9', color: '#64748b', width: 48, height: 48 }}>
                                            <VpnKeyIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Teacher ID
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ color: '#475569', fontWeight: 700, fontFamily: 'monospace' }}>
                                                {currentUser?._id?.slice(-8)}...
                                            </Typography>
                                        </Box>
                                    </InfoBox>
                                </Grid>
                            </Grid>
                        </PremiumCard>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default TeacherProfile;
